<script lang="ts">
  import { onMount } from 'svelte';
  import { push, replace } from 'svelte-spa-router';
  import { session } from '../lib/session';
  import { api, ApiError } from '../lib/api';
  import { adminState } from '../lib/admin-state';
  import IconPicker from '../components/IconPicker.svelte';
  import Icon from '../components/Icon.svelte';
  import type { IconName } from '../lib/icons';
  import MarkdownEditor from '../components/MarkdownEditor.svelte';
  import { renderMarkdown } from '../lib/markdown';

  let icon = $state<IconName>('leaf');
  let name = $state('');
  let description = $state('');
  let isActive = $state(true);
  let saving = $state(false);
  let error = $state<string | null>(null);
  let previewOpen = $state(false);

  onMount(() => {
    if (!$session) {
      replace('/');
      return;
    }
    if (!$session.isAdmin) {
      replace('/home');
      return;
    }
    // Read-only env (dev/local): creating sections mirrors to Bunny — not
    // allowed here.
    if (!$session.bunnyWritable) replace('/admin/dashboard');
  });

  const canSave = $derived(name.trim().length > 0);

  async function save(): Promise<void> {
    if (!canSave || saving) return;
    saving = true;
    error = null;
    try {
      await api.adminCreateCategory({
        icon,
        name: name.trim(),
        description: description.trim() || null,
        isActive,
      });
      adminState.activeTab = 'content';
      adminState.contentSubTab = 'categories';
      push('/admin/dashboard');
    } catch (err) {
      error =
        err instanceof ApiError
          ? `Не вдалось зберегти (${err.code})`
          : 'Не вдалось зберегти';
    } finally {
      saving = false;
    }
  }
</script>

<div class="screen">
  <div class="admin-header">
    <a href="#/admin/dashboard" class="back-link">← Назад</a>
    <div class="header-title">Новий розділ</div>
    <button
      type="button"
      class="header-preview"
      onclick={() => (previewOpen = true)}
      aria-label="Перегляд"
      title="Перегляд"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>
    </button>
  </div>
  <div class="scroll">
    <label class="field-label">Емодзі та назва</label>
    <div class="icon-name-row">
      <IconPicker bind:value={icon} />
      <input class="field name-input" maxlength="80" placeholder="Назва розділу" bind:value={name} />
    </div>

    <label class="field-label">Опис (необовʼязково)</label>
    <MarkdownEditor bind:value={description} maxlength={800} placeholder="Що в цьому розділі…" />

    <label class="field-label">Стан</label>
    <div class="status-pills">
      <button
        type="button"
        class="status-pill"
        class:active={!isActive}
        onclick={() => (isActive = false)}
      >Чернетка</button>
      <button
        type="button"
        class="status-pill"
        class:active={isActive}
        onclick={() => (isActive = true)}
      >Опубліковано</button>
    </div>

    {#if error}<div class="error">{error}</div>{/if}

    <button
      type="button"
      class="submit"
      disabled={!canSave || saving}
      onclick={save}
    >
      {saving ? 'Зберігаємо…' : 'Створити розділ'}
    </button>
  </div>

  {#if previewOpen}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" onclick={() => (previewOpen = false)}></div>
    <div class="preview-sheet" role="dialog" aria-modal="true" aria-label="Перегляд">
      <div class="modal-head">
        <div class="modal-title">Перегляд для користувача</div>
        <button type="button" class="modal-close" onclick={() => (previewOpen = false)}><Icon name="x" /></button>
      </div>
      <div class="modal-body preview-page">
        <div class="preview-topbar">
          <Icon name={icon} size="lg" /> {name.trim() || '(без назви)'}
        </div>
        <div class="preview-content">
          {#if description.trim()}
            <div class="promo">
              <div class="promo-text md-content">{@html renderMarkdown(description)}</div>
            </div>
          {/if}
          <div class="preview-state-row">
            <span class="preview-state-pill" class:pub={isActive}>
              {#if isActive}<Icon name="check" size="xs" /> Опубліковано{:else}◌ Чернетка{/if}
            </span>
          </div>
          <div class="preview-empty">Поки немає матеріалів — додаси після створення розділу</div>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .screen { display: flex; flex-direction: column; flex: 1; min-height: 0; background: var(--bg); }
  .admin-header {
    background: linear-gradient(135deg, var(--dark), var(--dark3));
    padding: 14px 18px;
    color: var(--text-light);
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }
  .back-link { font-size: 12px; color: rgba(251, 246, 236,0.9); text-decoration: none; }
  .header-title { flex: 1; text-align: center; font-size: 14px; font-weight: 600; }

  .scroll { flex: 1; overflow-y: auto; padding: 18px 18px 100px; }
  .field-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: var(--text-muted);
    margin: 12px 0 6px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  .field {
    width: 100%;
    border: 1px solid var(--border2);
    border-radius: 10px;
    padding: 11px 13px;
    font-size: 13px;
    color: var(--text);
    background: var(--bg);
    font-family: inherit;
    outline: none;
    margin-bottom: 4px;
  }
  .field:focus { border-color: var(--outline); }
  textarea.field { resize: vertical; min-height: 100px; }
  .icon-name-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .name-input { flex: 1; margin-bottom: 0; }

  .status-pills {
    display: flex;
    gap: 6px;
    margin: 4px 0 8px;
  }
  .status-pill {
    flex: 1;
    border: 1px solid var(--border2);
    background: var(--bg);
    color: var(--text-muted);
    padding: 9px 12px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
  }
  .status-pill.active {
    background: var(--pu);
    color: var(--text-light);
    border-color: var(--pu);
  }
  .error {
    margin: 12px 0;
    padding: 10px 14px;
    background: var(--danger-bg);
    color: var(--status-inactive);
    border: 1px solid var(--danger-border);
    border-radius: 10px;
    font-size: 12px;
  }
  .submit {
    width: 100%;
    margin-top: 18px;
    padding: 14px;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    color: var(--text-light);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
    transition: opacity 0.15s;
  }
  .submit:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Preview button in header (eye icon) */
  .header-preview {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: rgba(251, 246, 236, 0.15);
    border: 1px solid rgba(251, 246, 236, 0.25);
    color: var(--text-light);
    cursor: pointer;
    font-family: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .header-preview:hover { background: rgba(251, 246, 236, 0.25); }
  .header-preview svg { color: var(--text-light); display: block; }

  /* Preview modal — mirrors AdminCategoryEditPage so the new + edit views
     look identical when admin is verifying the layout. */
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    z-index: 1000;
  }
  .preview-sheet {
    position: fixed;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: min(100%, 480px);
    max-height: 92vh;
    background: var(--bg);
    border-radius: 16px 16px 0 0;
    z-index: 1001;
    display: flex;
    flex-direction: column;
    box-shadow: 0 -16px 40px rgba(0, 0, 0, 0.25);
    animation: sheet-up 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  }
  @keyframes sheet-up {
    from { transform: translate(-50%, 100%); }
    to { transform: translate(-50%, 0); }
  }
  .modal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 18px 12px;
    border-bottom: 0.5px solid var(--border);
    flex-shrink: 0;
  }
  .modal-title { font-size: 16px; font-weight: 700; color: var(--text); }
  .modal-close {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: var(--bg2);
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    font-size: 14px;
    font-family: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .modal-body {
    overflow-y: auto;
    flex: 1;
    min-height: 0;
  }
  .preview-page { padding: 0; }
  .preview-topbar {
    padding: 12px 18px;
    background: linear-gradient(135deg, var(--dark), var(--dark3));
    color: var(--text-light);
    font-size: 14px;
    font-weight: 600;
    text-align: center;
  }
  .preview-content { padding: 16px 16px 100px; }
  .preview-content .promo {
    padding: 14px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    margin-bottom: 16px;
  }
  .preview-content .promo-text {
    font-size: 13px;
    line-height: 1.55;
    color: var(--text);
  }
  .preview-state-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 16px;
  }
  .preview-state-pill {
    font-size: 11px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 999px;
    background: var(--bg2);
    color: var(--text-muted);
    border: 1px solid var(--border);
  }
  .preview-state-pill.pub {
    background: var(--green-light);
    color: var(--green);
    border-color: var(--green-light);
  }
  .preview-empty {
    text-align: center;
    padding: 30px 20px;
    color: var(--text-muted);
    font-size: 13px;
    background: var(--bg2);
    border: 1px dashed var(--border2);
    border-radius: 12px;
  }
</style>
